import { ref, onUnmounted } from 'vue';

// Globales Audio-Element (singleton)
let audioElement: HTMLAudioElement | null = null;

export function useAudioPreview() {
  const isPlaying = ref(false);
  const currentUrl = ref<string | null>(null);
  const duration = ref(0);
  const currentTime = ref(0);

  /**
   * Initialisiert das Audio-Element
   */
  function initAudio() {
    if (!audioElement) {
      audioElement = new Audio();
      
      audioElement.addEventListener('play', () => {
        isPlaying.value = true;
      });

      audioElement.addEventListener('pause', () => {
        isPlaying.value = false;
      });

      audioElement.addEventListener('ended', () => {
        isPlaying.value = false;
      });

      audioElement.addEventListener('timeupdate', () => {
        if (audioElement) {
          currentTime.value = audioElement.currentTime;
        }
      });

      audioElement.addEventListener('loadedmetadata', () => {
        if (audioElement) {
          duration.value = audioElement.duration;
        }
      });
    }
  }

  /**
   * Lädt eine Preview-URL
   */
  function load(previewUrl: string) {
    initAudio();
    
    if (!audioElement) return;

    // Vorherige Wiedergabe stoppen
    audioElement.pause();
    audioElement.currentTime = 0;

    // Neue URL laden
    audioElement.src = previewUrl;
    currentUrl.value = previewUrl;
    audioElement.load();
  }

  /**
   * Startet die Wiedergabe
   */
async function play() {
  initAudio();
  
  if (!audioElement || !currentUrl.value) {
    console.error('[AudioPreview.play] Kein Audio geladen', {
      hasAudioElement: !!audioElement,
      currentUrl: currentUrl.value
    });
    return;
  }

  console.log('[AudioPreview.play] Starte Preview...', currentUrl.value);

  try {
    const playPromise = audioElement.play();
    if (playPromise !== undefined) {
      await playPromise;
    }
    console.log('[AudioPreview.play] audio.play() resolved');
  } catch (error) {
    console.error('[AudioPreview.play] audio.play() rejected:', error);
  }
}


  /**
   * Pausiert die Wiedergabe
   */
  function pause() {
    if (audioElement) {
      audioElement.pause();
    }
  }

  /**
   * Stoppt die Wiedergabe und setzt zurück
   */
  function stop() {
    if (audioElement) {
      audioElement.pause();
      audioElement.currentTime = 0;
    }
  }

  /**
   * Setzt die Lautstärke (0.0 - 1.0)
   */
  function setVolume(volume: number) {
    if (audioElement) {
      audioElement.volume = Math.max(0, Math.min(1, volume));
    }
  }

  // Cleanup
  onUnmounted(() => {
    // Audio-Element nicht zerstören - ist global
  });

  return {
    isPlaying,
    currentUrl,
    duration,
    currentTime,
    load,
    play,
    pause,
    stop,
    setVolume
  };
}